import React, { useState } from 'react'
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from 'react-native'
import { Ionicons } from '@expo/vector-icons'

// utils
import { addLeadingZero, scheduleTimeRange } from '../../utils'

// components
import ModalPopup from '../shared/modal/ModalPopup'
import CustomButton from '../shared/button/Button'
import {
  colorPalette,
  fontPalette,
  globalStyles
} from '../../static/styles/global'
import MyScrollView from '../shared/navigator/MyScrollView'
//translation
import { useTranslation } from 'react-i18next'

interface timeProps {
  id?: number
  hour: number
  minute: number
  period: 'am' | 'pm'
  selected?: boolean
  action: any
  disable?: boolean
}
type timeEntry = Pick<
  timeProps,
  Exclude<keyof timeProps, 'selected' | 'action' | 'disable'>
>

const Time = ({
  id,
  hour,
  minute,
  period,
  selected,
  action,
  disable
}: timeProps): JSX.Element => {
  return (
    <Pressable
      onPress={action}
      style={[styles.time, selected && styles.selectedTime]}
      disabled={disable}
    >
      <Text
        style={[
          styles.timeText,
          disable && styles.timeTextDisabled,
          selected && styles.selectedTimeText
        ]}
      >
        {addLeadingZero(hour)}:{addLeadingZero(minute)} {period.toUpperCase()}
      </Text>
    </Pressable>
  )
}

interface scheduleTimePickerProps {
  title?: string
  visible: boolean
  setVisible: any
  onSelect?: (option: string) => void
  pickTimeFrom?: number
  setPickTimeFrom?: any
  selectedItem?: number | null
  setSelectedItem?: any
}

const ScheduleTimePicker = ({
  title,
  visible,
  setVisible,
  onSelect,
  pickTimeFrom = 0,
  setPickTimeFrom,
  selectedItem,
  setSelectedItem
}: scheduleTimePickerProps): JSX.Element => {
  // const [selectedItem, setSelectedItem] = useState(null)
  const [selectedTime, setSelectedTime] = useState<string>('')
  const { t } = useTranslation()
  const handleSetTime = (): void => {
    setVisible(!visible)
    onSelect(selectedTime)
    if (setPickTimeFrom) setPickTimeFrom(selectedItem)
  }

  const TimeRange = (): any => {
    const handleItemClick = (time: timeEntry): void => {
      setSelectedItem(time.id)
      const timeDisplay = `${addLeadingZero(time.hour)}:${addLeadingZero(
        time.minute
      )} ${time.period}`
      setSelectedTime(timeDisplay)
    }

    return scheduleTimeRange.map((time) => {
      return (
        <Time
          hour={time.hour}
          minute={time.minute}
          period={time.period}
          key={time.id}
          selected={selectedItem === time.id ? true : false}
          action={() => handleItemClick(time)}
          disable={time.id <= pickTimeFrom}
        />
      )
    })
  }

  return (
    <ModalPopup
      visible={visible}
      position={'bottom'}
      screen={'full'}
      flatBottom={false}
    >
      <View style={styles.modalHeader}>
        <Text
          style={[
            globalStyles.text,
            globalStyles.fs18,
            globalStyles.fwBold,
            globalStyles.blackTextColor
          ]}
        >
          {title}
        </Text>
        <TouchableOpacity onPress={() => setVisible(!visible)}>
          <Ionicons name="close-sharp" size={24} color={colorPalette.black} />
        </TouchableOpacity>
      </View>
      <View style={[styles.modalBody]}>
        <View style={[globalStyles.hDirection, styles.modalBodyInner]}>
          <View style={[globalStyles.flexOne]}>
            <MyScrollView>{TimeRange()}</MyScrollView>
          </View>
          {/* time */}
        </View>
        <View style={[globalStyles.horizontalMarGap, globalStyles.mb2]}>
          <View style={[globalStyles.mb1]}>
            <CustomButton
              text={t('settime')}
              onPress={handleSetTime}
              color={'secondary'}
            />
          </View>
          <CustomButton
            text={t('cancel')}
            onPress={() => {
              setVisible(!visible)
            }}
            reverse={true}
            light={true}
          />
        </View>
      </View>
    </ModalPopup>
  )
}

const styles = StyleSheet.create({
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderColor: colorPalette.darkLight
  },
  modalBody: {
    paddingVertical: 0,
    paddingHorizontal: 0
  },
  modalBodyInner: {
    padding: 16,
    minHeight: 100,
    maxHeight: 200
  },
  time: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 30,
    paddingVertical: 10
  },
  selectedTime: {
    backgroundColor: colorPalette.light,
    borderRadius: 50
  },
  timeText: {
    fontFamily: fontPalette.primaryRegular,
    fontSize: 15,
    color: colorPalette.black
  },
  timeTextDisabled: {
    color: colorPalette.lightGray
  },
  selectedTimeText: {
    color: colorPalette.black
  },
  timeSeperator: {
    flex: 1,
    width: 1,
    borderRightWidth: 1,
    borderColor: colorPalette.lightGray
  }
})

export default ScheduleTimePicker
